import { Bell, CircleDot, Tag, Users } from 'lucide-react'
import { Fragment, type ReactNode, useState } from 'react'
import { toast } from 'sonner'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { CardCheckbox } from '@/components/ui/CardCheckbox'
import { Checkbox } from '@/components/ui/checkbox'
import { DialogFooter } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  LEAD_ACTION_LABELS,
  LeadAction,
  useCreateLeadActionAutomation,
  useLeadActionPreview,
} from '@/models/leadActionAutomation'
import { buildSalesLeadParams, type SalesLeadFilters, SalesLeadStatus } from '@/models/salesLead'
import {
  type ClosedLostDraft,
  ClosedLostReasonFields,
  EMPTY_CLOSED_LOST_DRAFT,
  isClosedLostDraftComplete,
} from './ClosedLostReasonFields'
import { LeadActionAssigneePicker } from './LeadActionAssigneePicker'
import { LeadActionStatusPicker } from './LeadActionStatusPicker'
import { LeadActionTagPicker } from './LeadActionTagPicker'

interface ActionOption {
  action: LeadAction
  icon: ReactNode
  description: string
}

const ACTION_OPTIONS: ActionOption[] = [
  {
    action: LeadAction.GEICO_ELIGIBILITY,
    icon: <img src="https://static.cai.insure/carriers/geico-icon.png" alt="" className="size-4" />,
    description:
      'Tags each lead geico-eligible or geico-not-eligible. Leads without a DOT number or ZIP are skipped.',
  },
  {
    action: LeadAction.COVERWHALE_ELIGIBILITY,
    icon: <img src="https://static.cai.insure/carriers/coverwhale-icon.png" alt="" className="size-4" />,
    description:
      'Tags each lead coverwhale-eligible or coverwhale-not-eligible. Leads without a DOT number are skipped.',
  },
  {
    action: LeadAction.FOLLOW,
    icon: <Bell className="size-4" />,
    description: 'Follows each lead, so its new activity shows up in your notifications.',
  },
  {
    action: LeadAction.ADD_TAG,
    icon: <Tag className="size-4" />,
    description: 'Adds the tags you pick to each lead.',
  },
  {
    action: LeadAction.ASSIGN,
    icon: <Users className="size-4" />,
    description: 'Assigns each lead to the person you pick.',
  },
  {
    action: LeadAction.CHANGE_STATUS,
    icon: <CircleDot className="size-4" />,
    description: 'Moves each lead to the status you pick.',
  },
]

const listFormat = new Intl.ListFormat('en', { type: 'conjunction' })

interface LeadActionAutomationCreateProps {
  filters: SalesLeadFilters
  onDone: () => void
}

export function LeadActionAutomationCreate({ filters, onDone }: LeadActionAutomationCreateProps) {
  const [selected, setSelected] = useState<LeadAction[]>([])
  const [tagIds, setTagIds] = useState<string[]>([])
  const [assigneeId, setAssigneeId] = useState<string | null>(null)
  const [targetStatus, setTargetStatus] = useState<SalesLeadStatus | null>(null)
  const [closedLost, setClosedLost] = useState<ClosedLostDraft>(EMPTY_CLOSED_LOST_DRAFT)
  const [confirmed, setConfirmed] = useState(false)
  const { data: preview } = useLeadActionPreview(filters)
  const create = useCreateLeadActionAutomation()

  const actions = ACTION_OPTIONS.map((option) => option.action).filter((action) => selected.includes(action))
  const count = preview?.count ?? null
  const needsConfirmation = !!preview?.requiresConfirmation
  const addsTags = actions.includes(LeadAction.ADD_TAG)
  const assigns = actions.includes(LeadAction.ASSIGN)
  const changesStatus = actions.includes(LeadAction.CHANGE_STATUS)
  const closesAsLost = changesStatus && targetStatus === SalesLeadStatus.CLOSED_LOST
  const hasTargets =
    (!addsTags || tagIds.length > 0) &&
    (!assigns || !!assigneeId) &&
    (!changesStatus || !!targetStatus) &&
    (!closesAsLost || isClosedLostDraftComplete(closedLost))
  const canRun = actions.length > 0 && !!count && (!needsConfirmation || confirmed) && hasTargets

  function toggle(action: LeadAction, checked: boolean) {
    setSelected((current) => (checked ? [...current, action] : current.filter((a) => a !== action)))
  }

  async function run() {
    try {
      const runs = await create.mutateAsync({
        actions,
        leadFilters: buildSalesLeadParams(filters),
        confirmed,
        tagIds: addsTags ? tagIds : [],
        assigneeId: assigns ? assigneeId : null,
        targetStatus: changesStatus ? targetStatus : null,
        closedLostReason: closesAsLost ? closedLost.reason : null,
        transitionNotes: closesAsLost ? closedLost.notes.trim() : '',
      })
      const labels = listFormat.format(runs.map((automation) => LEAD_ACTION_LABELS[automation.action]))
      toast.success(`${labels} started for ${runs[0].leadCounts.total} leads`)
      onDone()
    } catch (err) {
      const message = (err as { response?: { data?: { error?: string } } }).response?.data?.error
      toast.error(message ?? 'Failed to start the automation')
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label>Actions</Label>
        {ACTION_OPTIONS.map((option) => (
          <Fragment key={option.action}>
            <CardCheckbox
              isChecked={selected.includes(option.action)}
              onChange={(checked) => toggle(option.action, checked)}
            >
              <div className="flex items-center gap-2">
                {option.icon}
                {LEAD_ACTION_LABELS[option.action]}
              </div>
              <div className="text-muted-foreground mt-0.5 text-sm font-normal">{option.description}</div>
            </CardCheckbox>
            {option.action === LeadAction.ADD_TAG && addsTags && (
              <LeadActionTagPicker selectedIds={tagIds} onChange={setTagIds} />
            )}
            {option.action === LeadAction.ASSIGN && assigns && (
              <LeadActionAssigneePicker value={assigneeId} onChange={setAssigneeId} />
            )}
            {option.action === LeadAction.CHANGE_STATUS && changesStatus && (
              <LeadActionStatusPicker value={targetStatus} onChange={setTargetStatus} />
            )}
            {option.action === LeadAction.CHANGE_STATUS && closesAsLost && (
              <ClosedLostReasonFields idPrefix="bulk" draft={closedLost} onChange={setClosedLost} />
            )}
          </Fragment>
        ))}
      </div>

      <div>
        <Label className="mb-1.5 block">Leads matching the current filters</Label>
        <ul className="text-muted-foreground list-inside list-disc text-sm">
          {(preview?.summary ?? []).map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <div className="mt-4 text-sm">
          <span className="font-semibold">{count ?? '…'}</span> leads will be processed, one at a time.
        </div>
      </div>

      {needsConfirmation && (
        <div className="flex flex-col gap-3">
          <Banner variant="warning" small>
            This list has {count} leads, more than {preview.confirmationThreshold}. Check the box below to run
            it on all of them.
          </Banner>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={confirmed} onCheckedChange={(checked) => setConfirmed(checked === true)} />
            Run it on all {count} leads
          </label>
        </div>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onDone} disabled={create.isPending}>
          Cancel
        </Button>
        <Button onClick={run} disabled={!canRun} loading={create.isPending ? 'Starting…' : undefined}>
          Run
        </Button>
      </DialogFooter>
    </div>
  )
}
